import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App.tsx';
import { AuthProvider } from './contexts/AuthContext.tsx';
import { queryClient } from './lib/queryClient.ts';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { initGlobalErrorHandlers } from './lib/globalErrorHandlers.ts';
import { initCsrf } from './lib/csrf.ts';

// Initialize Sentry for BetterStack error tracking
const sentryDsn = import.meta.env.VITE_BETTERSTACK_ERROR_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    // Capture 100% of errors
    sampleRate: 1.0,
    // Disable performance tracing (we don't need it for frontend)
    tracesSampleRate: 0,
  });
}

// Initialize global error handlers for BetterStack logging
initGlobalErrorHandlers();

// Initialize CSRF protection (non-blocking)
initCsrf();

// Remove initial loader with fade animation
const loader = document.getElementById('initial-loader');
if (loader) {
  loader.classList.add('fade-out');
  setTimeout(() => loader.remove(), 200);
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
